import { metersToPixels } from '../constants.ts';
import type { Rng } from '../rng.ts';
import { DEFAULT_HULL, type HullDimensions } from '../hullSize.ts';
import type { MapDocument } from '../map/types.ts';
import { boatCollisionsActive, type BoatHit } from '../modules/host.ts';
import { advanceRace, finishProgress, type RaceState } from '../race.ts';
import type { CommitResult, FleetContext } from '../turn.ts';
import { planWithFeel, type TurnFeel } from '../turnFeel.ts';
import type { BoatState, Vec2 } from '../types.ts';
import { candidateDestinations, coastPoint } from './candidates.ts';
import {
  courseModel,
  prepareCourse,
  raceProgress,
  turnsToGo,
  type Course,
  type CourseModel,
} from './course.ts';
import { fastStep, fleetHulls, isLegalClick, stepContext, type FleetHull } from './fastStep.ts';
import { EXPERT_KNOBS, RIVAL_KNOBS, type BotLevelKnobs, type SearchKnobs } from './levels.ts';
import type { RivalSeat } from './pilot.ts';
import { planRoots, type RootPlan } from './planner.ts';
import {
  forecastRivals,
  predictedHulls,
  rivalsInReach,
  rivalTurnsWith,
  type RivalForecast,
} from './rivals.ts';

/**
 * Extra turns the fallback charges for a hit this boat would cause, on top of the slowdown the
 * landing already shows: when nothing is clean, take the softest hit, never a slam by choice.
 */
const PAID_HIT_TURNS: Readonly<Record<'medium' | 'hard', number>> = { medium: 1, hard: 3 };
/** Each 30 m/turn of closing speed in a forced hit costs one more turn: hit as slowly as possible. */
const HIT_SPEED_REF_PX = 300;
const FALLBACK_DENSITY = 96;
/**
 * The search after a brushing click: narrower than the root search, since it runs once per
 * contact click, but as deep, so the click is ranked on the same horizon as a clean one.
 */
const FOLLOW_KNOBS: Omit<SearchKnobs, 'depth'> = { width: 12, density: 24, rootDensity: 32 };
/** A block is only weighed while it costs this boat no more than this many turns. */
const MAX_BLOCK_COST_TURNS = 1;
/** One block cannot be credited with more than this many of a rival's turns. */
const BLOCK_GAIN_CAP_TURNS = 3;
/**
 * A rival's light search is noisy by about this many turns, so a block has to be worth more
 * than that before it is believed.
 */
const MIN_BLOCK_GAIN_TURNS = 0.5;
/** Rivals within this many turns-to-go, and this many reaches away, are worth blocking. */
const BLOCK_TURNS_APART = 3;
const BLOCK_REACHES = 4;

export type BotDecision = {
  destination: Vec2;
  /** Planned clicks after this one, for next turn's warm start. Empty after a fallback. */
  plan: Vec2[];
  /** True when no searched click survived the exact check and the fallback chose. */
  fallback: boolean;
  /** Chosen over a quicker clean click because it holds a rival up more than it costs. */
  blocked: boolean;
  /** The click touches a parked rival (a nudge). */
  contact: boolean;
  /** The click it meant, before aim error. Equals `destination` without aim error. */
  intended: Vec2;
  /**
   * Turns lost by slipping to a worse clean click than its best (`temperature`). 0 when it took
   * its best, or a fallback.
   */
  slipTurns: number;
};

export type DecideOptions = {
  knobs?: BotLevelKnobs;
  /** Parked hulls. Omitted on a solo run or a simultaneous lock-in. */
  fleet?: FleetContext;
  /** Every other seat with its race and feel (Sequential). Expert predicts and blocks them. */
  rivals?: readonly RivalSeat[];
  /** Last turn's `plan`. */
  warm?: readonly Vec2[];
  /**
   * This turn's random stream (`botTurnRng`). Only `temperature` and `aimErrorM` read it; without
   * one the bot sails its best click exactly.
   */
  rng?: Rng;
};

function accelOf(feel: TurnFeel): number {
  return feel.reach?.accel ?? feel.radius;
}

function brakeOf(feel: TurnFeel): number {
  return feel.reach?.brake ?? feel.radius;
}

function reachOf(feel: TurnFeel): number {
  return Math.max(feel.radius, feel.reach?.accel ?? 0, feel.reach?.turn ?? 0);
}

/** A hit this seat is at fault for (or a head-on) that costs it speed. */
function paysFor(hit: BoatHit, seatId: number | undefined): boolean {
  return hit.severity !== 'nudge' && (hit.faultId === null || hit.faultId === (seatId ?? -1));
}

type Candidate = RootPlan & { contact: boolean };

type Verified = {
  plan: Candidate;
  exact: CommitResult;
};

/**
 * The Expert click: search the fast model, then commit the best click whose exact `planMove`
 * agrees with it — no shore, the jobs the search expected, and no boat contact beyond what this
 * level allows (none for a plain level; a nudge for one that squeezes). With none, the least-bad
 * exact landing.
 *
 * With rivals (Sequential, collisions on), the bot also:
 * - with `knobs.anticipate`, predicts each rival's line and steers around where it will be parked
 *   on later moves, and weighs its best clean clicks by how much each holds a close rival up
 *   (`aggression`);
 * - with `knobs.squeeze` (unset follows `anticipate`), tries clicks that brush a parked rival
 *   against the real sweep, since a nudge is free.
 */
export function decideMove(
  course: Course,
  feel: TurnFeel,
  boat: BoatState,
  race: RaceState,
  options: DecideOptions = {},
): BotDecision {
  const knobs = options.knobs ?? EXPERT_KNOBS;
  const map = course.map;
  const model = courseModel(course, feel);
  const rivals = options.rivals ?? [];
  // With collisions off the hulls pass through each other: rivals are only boats to beat, never
  // anything to dodge, follow at a distance, or block.
  const racing = rivals.length > 0 && boatCollisionsActive();
  const reading = (knobs.anticipate ?? false) && racing;
  const squeezing = (knobs.squeeze ?? knobs.anticipate ?? false) && racing;
  const rivalKnobs = knobs.rival ?? RIVAL_KNOBS;
  const ownHull = options.fleet?.hull ?? DEFAULT_HULL;
  const ctx = stepContext(map, feel, course.raster, ownHull);
  // Forecasting rivals only pays for itself when something reads the forecast.
  const forecasting =
    reading &&
    ((knobs.forecastHulls ?? true) || (knobs.followDistance ?? true) || (knobs.aggression ?? 0) > 0);
  const forecasts: RivalForecast[] = forecasting ? forecastRivals(course, rivals, rivalKnobs) : [];
  const predicted = forecasting
    ? predictedHulls(forecasts, knobs.depth, {
        forecast: knobs.forecastHulls ?? true,
        brake: knobs.followDistance ?? true,
      })
    : undefined;
  const touching: Vec2[] = [];
  const planInput = {
    ctx,
    model,
    knobs,
    accelPx: accelOf(feel),
    brakePx: brakeOf(feel),
    discs: fleetHulls(options.fleet),
    predicted,
    touching: squeezing ? touching : undefined,
    warm: options.warm,
  };
  const candidates: Candidate[] = planRoots(boat, race, planInput).map((plan) => ({
    ...plan,
    contact: false,
  }));

  if (squeezing && touching.length > 0) {
    candidates.push(
      ...contactCandidates(model, feel, boat, race, touching, options.fleet, predicted ?? [], knobs),
    );
    candidates.sort((a, b) => (better(a, b) ? -1 : better(b, a) ? 1 : 0));
  }

  const verified = verifyCandidates(
    course,
    feel,
    boat,
    race,
    candidates,
    options.fleet,
    squeezing,
    knobs.verify,
    Math.max(
      reading ? Math.max(1, knobs.blockRoots ?? 1) : 1,
      (knobs.temperature ?? 0) > 0 ? SLIP_CHOICES : 1,
    ),
  );

  if (verified.length === 0) {
    const fallbackClick = leastBadClick(model, feel, boat, race, options.fleet);
    return {
      destination: fallbackClick,
      plan: [],
      fallback: true,
      blocked: false,
      contact: false,
      intended: fallbackClick,
      slipTurns: 0,
    };
  }

  const best = verified[0]!;
  const steered =
    reading && (knobs.aggression ?? 0) > 0 && verified.length > 1
      ? weighBlocks(
          course,
          feel,
          boat,
          race,
          model,
          verified,
          rivals,
          forecasts,
          rivalKnobs,
          ownHull,
          knobs,
        )
      : best;
  const chosen = slip(steered, verified, knobs.temperature ?? 0, options.rng);
  const destination = wobble(
    map,
    feel,
    boat,
    chosen.plan.destination,
    knobs.aimErrorM ?? 0,
    options.fleet,
    options.rng,
  );

  return {
    destination,
    plan: chosen.plan.line.slice(1),
    fallback: false,
    blocked: steered !== best,
    contact: chosen.exact.boatContacts.length > 0,
    intended: chosen.plan.destination,
    slipTurns: chosen === steered ? 0 : Math.max(0, chosen.plan.score - steered.plan.score),
  };
}

function better(a: Candidate, b: Candidate): boolean {
  if (a.finished !== b.finished) {
    return a.finished;
  }

  if (!a.finished && a.depth !== b.depth) {
    return a.depth > b.depth;
  }

  return a.score < b.score;
}

/**
 * First clicks that brush a parked rival. The fast model cannot shove, so the best few by a
 * quick estimate go through the real sweep (shove and penalty included). A click that would
 * cost this boat a medium or hard hit is dropped; a nudge is followed by a lighter search from
 * where the sweep actually leaves the boat.
 */
function contactCandidates(
  model: CourseModel,
  feel: TurnFeel,
  boat: BoatState,
  race: RaceState,
  touching: readonly Vec2[],
  fleet: FleetContext | undefined,
  predicted: readonly (readonly FleetHull[])[],
  knobs: BotLevelKnobs,
): Candidate[] {
  const course = model.course;
  const map = course.map;
  const ctx = stepContext(map, feel, course.raster);
  const accel = accelOf(feel);
  const brake = brakeOf(feel);
  const quick = touching
    .map((dest) => {
      const step = fastStep(ctx, boat, race, dest);
      return { dest, score: step ? 1 + turnsToGo(model, step.boat, step.race, accel, brake, knobs) : Infinity };
    })
    .filter((entry) => Number.isFinite(entry.score))
    .sort((a, b) => a.score - b.score)
    .slice(0, knobs.contactRoots ?? 0);
  const out: Candidate[] = [];

  for (const { dest } of quick) {
    const exact = planWithFeel(boat, dest, map, feel, fleet);

    if (!exact || exact.crash || exact.boatContacts.some((hit) => paysFor(hit, fleet?.id))) {
      continue;
    }

    const after = advanceRace(map, race, exact.path);
    const jobs = raceProgress(course, after);

    if (after.complete) {
      const at = finishProgress(map, race, exact.path) ?? 1;
      out.push({ destination: dest, score: at, depth: Infinity, finished: true, jobs, line: [dest], contact: true });
      continue;
    }

    const follow = planRoots(exact.boat, after, {
      ctx,
      model,
      knobs: { ...FOLLOW_KNOBS, depth: Math.max(1, knobs.depth - 1), verify: 0 },
      accelPx: accel,
      brakePx: brake,
      discs: predicted[0] ? [...predicted[0]] : [],
      predicted: predicted.slice(1),
    })[0];

    out.push({
      destination: dest,
      score: 1 + (follow ? follow.score : turnsToGo(model, exact.boat, after, accel, brake, knobs)),
      depth: 1 + (follow?.depth ?? 0),
      finished: follow?.finished ?? false,
      jobs,
      line: [dest, ...(follow?.line ?? [])],
      contact: true,
    });
  }

  return out;
}

/** Ranked clicks that pass the exact rules, up to `keep` of them, trying at most `limit`. */
function verifyCandidates(
  course: Course,
  feel: TurnFeel,
  boat: BoatState,
  race: RaceState,
  candidates: readonly Candidate[],
  fleet: FleetContext | undefined,
  allowNudge: boolean,
  limit: number,
  keep: number,
): Verified[] {
  const map = course.map;
  const out: Verified[] = [];

  for (const plan of candidates.slice(0, limit)) {
    const exact = planWithFeel(boat, plan.destination, map, feel, fleet);

    if (!exact || exact.crash) {
      continue;
    }

    const contactOk = allowNudge
      ? !exact.boatContacts.some((hit) => paysFor(hit, fleet?.id))
      : exact.boatContacts.length === 0;

    if (!contactOk || (!allowNudge && exact.clipped)) {
      continue;
    }

    if (raceProgress(course, advanceRace(map, race, exact.path)) < plan.jobs) {
      continue;
    }

    out.push({ plan, exact });

    if (out.length >= keep) {
      break;
    }
  }

  return out;
}

/**
 * Among clean clicks no more than `MAX_BLOCK_COST_TURNS` slower than the best, the one that
 * holds close rivals up the most per turn it costs: lowest `own turns − aggression × rivals'
 * extra turns`. A rival that runs into this boat pays the penalty, so its light search treats
 * the landing as a wall.
 */
function weighBlocks(
  course: Course,
  feel: TurnFeel,
  boat: BoatState,
  race: RaceState,
  model: CourseModel,
  verified: readonly Verified[],
  rivals: readonly RivalSeat[],
  forecasts: readonly RivalForecast[],
  rivalKnobs: SearchKnobs,
  ownHull: HullDimensions,
  knobs: BotLevelKnobs,
): Verified {
  const best = verified[0]!;
  const close = rivalsInReach(
    course,
    rivals,
    turnsToGo(model, boat, race, accelOf(feel), brakeOf(feel), knobs),
    boat.position,
    reachOf(feel) * BLOCK_REACHES,
    BLOCK_TURNS_APART,
  );

  if (close.length === 0) {
    return best;
  }

  const sameClass = verified.filter(
    (entry) =>
      entry.plan.finished === best.plan.finished &&
      (best.plan.finished || entry.plan.depth === best.plan.depth) &&
      entry.plan.score - best.plan.score <= (knobs.blockMaxCostTurns ?? MAX_BLOCK_COST_TURNS),
  );

  if (sameClass.length < 2) {
    return best;
  }

  const rivalTurns = (entry: Verified) =>
    close.map((rival) =>
      rivalTurnsWith(course, rival, rivals, rivalKnobs, {
        boat: entry.exact.boat,
        dimensions: ownHull,
      }),
    );
  const baseline = rivalTurns(best);
  let chosen = best;
  let chosenValue = best.plan.score;

  for (const entry of sameClass.slice(1)) {
    // Only a landing that sits on a rival's next move can hold it up; anything else is noise.
    const inTheWay = close.map((rival) =>
      onNextMove(forecasts, rival, entry.exact.boat.position, ownHull.lengthM),
    );

    if (!inTheWay.some(Boolean)) {
      continue;
    }

    const turns = rivalTurns(entry);
    const gain = turns.reduce(
      (sum, value, index) =>
        sum + (inTheWay[index] ? Math.min(BLOCK_GAIN_CAP_TURNS, value - baseline[index]!) : 0),
      0,
    );
    const value = entry.plan.score - (knobs.aggression ?? 0) * gain;

    if (gain >= (knobs.blockMinGainTurns ?? MIN_BLOCK_GAIN_TURNS) && value < chosenValue) {
      chosen = entry;
      chosenValue = value;
    }
  }

  return chosen;
}

/** True when `point` lies within a hull's reach of the rival's forecast next move. */
function onNextMove(
  forecasts: readonly RivalForecast[],
  rival: RivalSeat,
  point: Vec2,
  ownLengthM: number,
): boolean {
  const next = forecasts.find((forecast) => forecast.seat.id === rival.id)?.poses[0];

  if (!next) {
    return false;
  }

  const a = rival.boat.position;
  const b = next.position;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const span = dx * dx + dy * dy;
  const t = span > 0 ? Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / span)) : 0;
  const gap = Math.hypot(point.x - (a.x + dx * t), point.y - (a.y + dy * t));

  return gap <= metersToPixels((ownLengthM + rival.dimensions.lengthM) / 2);
}

/** Clean clicks a bot that slips may pick from. */
const SLIP_CHOICES = 6;

/**
 * A human-sized slip: among the clean clicks on the same horizon as `chosen`, pick one with
 * weight exp(−turns lost / temperature). Small slips are common, big ones rare; 0 never slips.
 */
function slip(chosen: Verified, verified: readonly Verified[], temperature: number, rng?: Rng): Verified {
  if (temperature <= 0 || !rng) {
    return chosen;
  }

  const peers = verified.filter(
    (entry) =>
      entry.plan.finished === chosen.plan.finished &&
      (chosen.plan.finished || entry.plan.depth === chosen.plan.depth),
  );

  if (peers.length < 2) {
    return chosen;
  }

  const base = Math.min(...peers.map((entry) => entry.plan.score));
  const weights = peers.map((entry) => Math.exp(-(entry.plan.score - base) / temperature));
  let roll = rng() * weights.reduce((sum, weight) => sum + weight, 0);

  for (let index = 0; index < peers.length; index += 1) {
    roll -= weights[index]!;

    if (roll <= 0) {
      return peers[index]!;
    }
  }

  return peers.at(-1)!;
}

/** Standard normal from two uniforms (Box–Muller). */
function gaussian(rng: Rng): number {
  const u = Math.max(rng(), 1e-12);
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * rng());
}

/**
 * Aim error: the click lands a little off where the bot meant it, along the move and half as
 * much across, more at speed. The wobbled click must stay legal and must not run this boat into
 * a paid hit on another; otherwise it is pulled back toward the intended click. Land is not
 * checked: a careless bot can scrape the shore, as people do.
 */
function wobble(
  map: MapDocument,
  feel: TurnFeel,
  boat: BoatState,
  intended: Vec2,
  aimErrorM: number,
  fleet: FleetContext | undefined,
  rng?: Rng,
): Vec2 {
  if (aimErrorM <= 0 || !rng) {
    return intended;
  }

  const sigma = metersToPixels(aimErrorM) * (0.5 + boat.speed / AIM_SPEED_REF_PX);
  const dx = intended.x - boat.position.x;
  const dy = intended.y - boat.position.y;
  const span = Math.hypot(dx, dy) || 1;
  const ux = dx / span;
  const uy = dy / span;
  const along = gaussian(rng) * sigma;
  const across = gaussian(rng) * sigma * 0.5;
  let dest = { x: intended.x + ux * along - uy * across, y: intended.y + uy * along + ux * across };

  for (let attempt = 0; attempt < 4; attempt += 1) {
    if (isLegalClick(map, feel, boat, dest)) {
      const exact = planWithFeel(boat, dest, map, feel, fleet);

      if (exact && !exact.boatContacts.some((hit) => paysFor(hit, fleet?.id))) {
        return dest;
      }
    }

    dest = { x: (dest.x + intended.x) / 2, y: (dest.y + intended.y) / 2 };
  }

  return intended;
}

/** Speed at which the aim error is 1.5× its base: 60 m/turn. */
const AIM_SPEED_REF_PX = 600;

function leastBadClick(
  model: CourseModel,
  feel: TurnFeel,
  boat: BoatState,
  race: RaceState,
  fleet: FleetContext | undefined,
): Vec2 {
  const map = model.course.map;
  const accel = accelOf(feel);
  const brake = brakeOf(feel);
  let best: { dest: Vec2; score: number } | null = null;

  for (const dest of candidateDestinations(boat, map, feel, FALLBACK_DENSITY)) {
    const exact = planWithFeel(boat, dest, map, feel, fleet);

    if (!exact) {
      continue;
    }

    const after = advanceRace(map, race, exact.path);
    const score =
      (after.complete ? 0 : 1 + turnsToGo(model, exact.boat, after, accel, brake)) +
      exact.boatContacts.reduce(
        (sum, hit) =>
          sum +
          (paysFor(hit, fleet?.id)
            ? PAID_HIT_TURNS[hit.severity as 'medium' | 'hard'] + hit.closingSpeed / HIT_SPEED_REF_PX
            : 0),
        0,
      );

    if (!best || score < best.score) {
      best = { dest, score };
    }
  }

  return best?.dest ?? coastPoint(boat, map, feel);
}

/**
 * A click for when the worker could not answer: every reach sample through the exact rules,
 * least turns to go wins, crashes included. Builds the course on first use.
 */
export function fallbackDestination(
  boat: BoatState,
  race: RaceState,
  map: MapDocument,
  feel: TurnFeel,
  fleet?: FleetContext,
): Vec2 {
  return leastBadClick(courseModel(prepareCourse(map), feel), feel, boat, race, fleet);
}
